import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

interface SiteContent {
  contact_hero_title?: string;
  contact_hero_subtitle?: string;
  contact_hero_image?: string;
  contact_hero_bg_video?: string;
  contact_hero_title_font?: string;
  contact_hero_subtitle_font?: string;
  contact_hero_title_size?: string;
  contact_hero_subtitle_size?: string;
  contact_form_title?: string;
  contact_form_subtitle?: string;
  contact_info_title?: string;
  contact_info_subtitle?: string;
  contact_info_email?: string;
  contact_info_phone?: string;
  contact_info_address?: string;
  contact_hero_bg_image?: string;
  contact_team_title?: string;
  contact_team_subtitle?: string;
  contact_member1_name?: string;
  contact_member1_role?: string;
  contact_member1_email?: string;
  contact_member1_phone?: string;
  contact_member1_image?: string;
  contact_member1_linkedin?: string;
  contact_member1_linkedin_enabled?: string;
  contact_member1_instagram?: string;
  contact_member1_instagram_enabled?: string;
  contact_member1_link1_label?: string;
  contact_member1_link1_url?: string;
  contact_member1_link1_enabled?: string;
  contact_member1_link2_label?: string;
  contact_member1_link2_url?: string;
  contact_member1_link2_enabled?: string;
  contact_member2_name?: string;
  contact_member2_role?: string;
  contact_member2_email?: string;
  contact_member2_phone?: string;
  contact_member2_image?: string;
  contact_member2_linkedin?: string;
  contact_member2_linkedin_enabled?: string;
  contact_member2_instagram?: string;
  contact_member2_instagram_enabled?: string;
  contact_member2_link1_label?: string;
  contact_member2_link1_url?: string;
  contact_member2_link1_enabled?: string;
  contact_member2_link2_label?: string;
  contact_member2_link2_url?: string;
  contact_member2_link2_enabled?: string;
}

interface CustomFont {
  font_family: string;
  font_display_name: string;
  woff2_url?: string;
  woff_url?: string;
  ttf_url?: string;
  otf_url?: string;
}

export default function Contact() {
  const [content, setContent] = useState<SiteContent>({});
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Form handling state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Default fallback content
  const defaultContent: SiteContent = {
    contact_hero_title: "Let's Create Something Amazing Together",
    contact_hero_subtitle: "Ready to bring your vision to life? We'd love to hear about your project and explore how we can collaborate to create something extraordinary.",
    contact_hero_image: "https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20natural%20lighting%2C%20minimalist%20design%2C%20professional%20workspace%20with%20plants%20and%20clean%20architecture%2C%20contemporary%20interior%20design%20for%20creative%20agency&width=1200&height=600&seq=contact-hero&orientation=landscape",
    contact_hero_bg_video: '',
    contact_hero_title_font: 'Inter',
    contact_hero_subtitle_font: 'Inter',
    contact_hero_title_size: 'text-5xl md:text-6xl',
    contact_hero_subtitle_size: 'text-xl',
    contact_form_title: 'Start a Conversation',
    contact_form_subtitle: "Tell us about your project and we'll get back to you within 24 hours.",
    contact_info_title: 'Get In Touch',
    contact_info_subtitle: "We're here to help bring your creative vision to life.",
    contact_info_email: 'hello@creativeduo.com',
    contact_info_phone: '+1 (555) 123-4567',
    contact_info_address: '123 Creative Street, Design District, NY 10001',
    contact_hero_bg_image: '',
    contact_team_title: 'Meet Our Team',
    contact_team_subtitle: 'The creative minds behind every project. Get in touch with us directly.',
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch('/admin/content-api.php');
        const data = await response.json();

        if (data.success) {
          setContent({ ...defaultContent, ...data.data });
          
          // Fetch custom fonts if available
          if (data.custom_fonts) {
            setCustomFonts(data.custom_fonts);
          }
        } else {
          setContent(defaultContent);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
        setContent(defaultContent);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Load custom fonts and Google Fonts with Safari compatibility
  useEffect(() => {
    const loadFonts = async () => {
      // Load custom fonts
      if (customFonts.length > 0) {
        const style = document.createElement('style');
        let cssRules = '';
        
        customFonts.forEach(font => {
          const sources = [];
          if (font.woff2_url) sources.push(`url('${font.woff2_url}') format('woff2')`);
          if (font.woff_url) sources.push(`url('${font.woff_url}') format('woff')`);
          if (font.ttf_url) sources.push(`url('${font.ttf_url}') format('truetype')`);
          if (font.otf_url) sources.push(`url('${font.otf_url}') format('opentype')`);
          
          if (sources.length > 0) {
            cssRules += `
              @font-face {
                font-family: '${font.font_family}';
                src: ${sources.join(', ')};
                font-display: swap;
                font-weight: normal;
                font-style: normal;
              }
            `;
          }
        });
        
        if (cssRules) {
          style.textContent = cssRules;
          document.head.appendChild(style);
        }
      }

      // Load Google Fonts for contact page fonts
      const googleFontsToLoad = new Set<string>();
      
      if (content.contact_hero_title_font && content.contact_hero_title_font !== 'Inter' && !customFonts.find(f => f.font_family === content.contact_hero_title_font)) {
        googleFontsToLoad.add(content.contact_hero_title_font);
      }
      
      if (content.contact_hero_subtitle_font && content.contact_hero_subtitle_font !== 'Inter' && !customFonts.find(f => f.font_family === content.contact_hero_subtitle_font)) {
        googleFontsToLoad.add(content.contact_hero_subtitle_font);
      }

      // Load Google Fonts with better Safari compatibility
      const loadGoogleFont = (fontName: string) => {
        return new Promise<void>((resolve) => {
          const fontNameForUrl = fontName.replace(/\s+/g, '+');
          const existingLink = document.querySelector(`link[href*="${fontNameForUrl}"]`);
          
          if (!existingLink) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${fontNameForUrl}:wght@400;500;600;700;800;900&display=swap`;
            link.rel = 'stylesheet';
            link.crossOrigin = 'anonymous';
            
            // Add load event listener for Safari compatibility
            link.onload = () => {
              console.log(`Contact page font loaded: ${fontName}`);
              resolve();
            };
            
            link.onerror = () => {
              console.warn(`Failed to load contact page font: ${fontName}`);
              resolve();
            };
            
            document.head.appendChild(link);
            
            // Fallback timeout for Safari
            setTimeout(() => {
              resolve();
            }, 3000);
          } else {
            resolve();
          }
        });
      };

      // Load all Google Fonts
      const fontPromises = Array.from(googleFontsToLoad).map(fontName => loadGoogleFont(fontName));
      
      try {
        await Promise.all(fontPromises);
        
        // Force font loading check for Safari
        if (googleFontsToLoad.size > 0) {
          // Use document.fonts API if available (modern browsers including Safari)
          if ('fonts' in document) {
            try {
              await (document as any).fonts.ready;
              console.log('All contact page fonts loaded successfully');
            } catch (error) {
              console.warn('Contact page font loading check failed:', error);
            }
          }
        }
      } catch (error) {
        console.warn('Error loading contact page fonts:', error);
      }
    };

    if (!loading && (content.contact_hero_title_font || content.contact_hero_subtitle_font)) {
      loadFonts();
    }
  }, [content, customFonts, loading]);

  const handleVideoError = () => {
    setVideoError(true);
  };

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
  };

  // Function to check if URL is a Vimeo embed URL
  const isVimeoEmbed = (url: string) => {
    return url.includes('player.vimeo.com');
  };

  // Function to prepare Vimeo URL for autoplay
  const prepareVimeoUrl = (url: string) => {
    if (!isVimeoEmbed(url)) return url;

    // Add autoplay and mute parameters for Vimeo
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}autoplay=1&muted=1&loop=1&background=1&controls=0`;
  };

  // Dynamic styles for typography with Safari font fallback
  const heroTitleStyle = {
    fontFamily: content.contact_hero_title_font ? `"${content.contact_hero_title_font}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined,
  };

  const heroSubtitleStyle = {
    fontFamily: content.contact_hero_subtitle_font ? `"${content.contact_hero_subtitle_font}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined,
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);

    // Validate required fields using current state
    if (!formData.name?.trim() || !formData.email?.trim() || !formData.message?.trim()) {
      console.log('Validation failed - missing required fields');
      setSubmitStatus('error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      console.log('Validation failed - invalid email format');
      setSubmitStatus('error');
      return;
    }

    // Validate message length
    if (formData.message.length > 500) {
      console.log('Validation failed - message too long');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('subject', formData.subject?.trim() || '');
      formDataToSend.append('message', formData.message.trim());

      console.log('Sending form data:', Object.fromEntries(formDataToSend));

      // Submit to your own server endpoint
      const response = await fetch('/admin/contact-handler.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend,
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSubmitStatus('success');
          setFormData({ name: '', email: '', subject: '', message: '' });
        } else {
          console.log('Server error:', result.error);
          setSubmitStatus('error');
        }
      } else {
        console.log('HTTP error:', response.status);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to render text with line breaks
  const renderTextWithBreaks = (text: string) => {
    return text.split('<br>').map((line, index, array) => (
      <span key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </span>
    ));
  };

  // Team members data
  const teamMembers = [
    {
      name: content.contact_member1_name || 'Sarah Johnson',
      role: content.contact_member1_role || 'Art Director',
      email: content.contact_member1_email || 'sarah@creativeduo.com',
      phone: content.contact_member1_phone || '',
      image: content.contact_member1_image || `https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20art%20director%2C%20creative%20professional%2C%20modern%20studio%20lighting%2C%20clean%20background%2C%20confident%20expression%2C%20business%20casual%20attire&width=838&height=356&seq=team-0&orientation=landscape`,
      linkedin: content.contact_member1_linkedin || 'https://linkedin.com/in/sarahjohnson',
      instagram: content.contact_member1_instagram || 'https://instagram.com/sarahjohnson'
    },
    {
      name: content.contact_member2_name || 'Michael Chen',
      role: content.contact_member2_role || 'Creative Copywriter',
      email: content.contact_member2_email || 'michael@creativeduo.com',
      phone: content.contact_member2_phone || '',
      image: content.contact_member2_image || `https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20creative%20copywriter%2C%20creative%20professional%2C%20modern%20studio%20lighting%2C%20clean%20background%2C%20confident%20expression%2C%20business%20casual%20attire&width=838&height=356&seq=team-1&orientation=landscape`,
      linkedin: content.contact_member2_linkedin || 'https://linkedin.com/in/michaelchen',
      instagram: content.contact_member2_instagram || 'https://instagram.com/michaelchen'
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Roel & Wietse Creative Team",
    "description": "Get in touch with our creative team. Ready to bring your vision to life through compelling storytelling and exceptional design.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Roel & Wietse Creative Team",
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": content.contact_info_email || "hello@creativeduo.com",
          "telephone": content.contact_info_phone || "+1 (555) 123-4567",
          "areaServed": "NL",
          "availableLanguage": ["Dutch", "English"]
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": content.contact_info_address || "123 Creative Street",
        "addressLocality": "Amsterdam",
        "addressCountry": "NL"
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Contact Us - Roel & Wietse Creative Team"
        description="Get in touch with our creative team. Ready to bring your vision to life through compelling storytelling and exceptional design."
        keywords="Contact, Creative team, Get in touch, Project inquiry, Art direction, Copywriting services"
        schemaData={schemaData}
      />
      <Navigation />

      {/* Hero Section with Video/Image Support */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image (Always present as backup) */}
        <div className="absolute inset-0">
          <div
            className="w-full h-full bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${content.contact_hero_image}')`,
              zIndex: 1,
            }}
          />
        </div>

        {/* Background Video (Overlays the image when loaded and available) */}
        {!videoError && content.contact_hero_bg_video && (
          <div className="absolute inset-0" style={{ zIndex: videoLoaded ? 2 : 0 }}>
            {isVimeoEmbed(content.contact_hero_bg_video) ? (
              // Vimeo iframe embed
              <iframe
                src={prepareVimeoUrl(content.contact_hero_bg_video)}
                className="w-full h-full object-cover"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '100vw',
                  height: '56.25vw',
                  minHeight: '100vh',
                  minWidth: '177.77vh',
                  transform: 'translate(-50%, -50%)',
                }}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                onError={handleVideoError}
                onLoad={handleVideoLoaded}
              />
            ) : (
              // Regular video file
              <video
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onError={handleVideoError}
                onLoadedData={handleVideoLoaded}
                onCanPlay={handleVideoLoaded}
                onLoadStart={() => {
                  const video = document.querySelector('video');
                  if (video) {
                    video
                      .play()
                      .catch(() => {
                        console.log('Video autoplay failed, showing fallback image');
                        setVideoError(true);
                      });
                  }
                }}
              >
                <source src={content.contact_hero_bg_video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        )}

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/40" style={{ zIndex: 3 }}></div>

        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-8 text-center" style={{ zIndex: 10 }}>
          <h1
            className={`${content.contact_hero_title_size || 'text-5xl md:text-6xl'} font-bold text-white mb-6`}
            style={heroTitleStyle}
          >
            {renderTextWithBreaks(content.contact_hero_title || "Let's Create Something Amazing Together")}
          </h1>
          <p
            className={`${content.contact_hero_subtitle_size || 'text-xl'} text-white/90 max-w-3xl mx-auto leading-relaxed`}
            style={heroSubtitleStyle}
          >
            {renderTextWithBreaks(content.contact_hero_subtitle || "Ready to bring your vision to life? We'd love to hear about your project and explore how we can collaborate to create something extraordinary.")}
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all text-sm"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all text-sm"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message * <span className="text-gray-500 text-xs">(Max 500 characters)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all text-sm resize-none"
                  placeholder="Tell us about your project, goals, timeline, and any specific requirements..."
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-600 mr-2"></i>
                    <p className="text-green-800 text-sm">
                      Thank you! Your message has been sent successfully. We'll get back to you soon.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 mr-2"></i>
                    <p className="text-red-800 text-sm">
                      Please fill in all required fields with valid information and ensure your message is under 500 characters.
                    </p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white py-4 px-8 rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </span>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              {content.contact_team_title || 'Meet Our Team'}
            </h2>
            <p className="text-lg text-gray-300">
              {content.contact_team_subtitle || 'The creative minds behind every project. Get in touch with us directly.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white p-8 shadow-lg rounded-lg">
                <div className="w-full mb-6 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-auto object-cover"
                    style={{ aspectRatio: '838/356' }}
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-gray-600 mb-4">{member.role}</p>
                  <div className="space-y-3">
                    <a 
                      href={`mailto:${member.email}`} 
                      className="flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors cursor-pointer group"
                    >
                      <i className="ri-mail-line mr-3 text-lg group-hover:scale-110 transition-transform"></i>
                      <span className="font-medium">{member.email}</span>
                    </a>

                    {member.phone && (
                      <a 
                        href={`tel:${member.phone}`} 
                        className="flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors cursor-pointer group"
                      >
                        <i className="ri-phone-line mr-3 text-lg group-hover:scale-110 transition-transform"></i>
                        <span className="font-medium">{member.phone}</span>
                      </a>
                    )}

                    <div className="flex justify-center space-x-4 pt-2">
                      {member.linkedin && (
                        <a 
                          href={member.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
                        >
                          <i className="ri-linkedin-fill text-xl"></i>
                        </a>
                      )}
                      
                      {member.instagram && (
                        <a 
                          href={member.instagram} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
                        >
                          <i className="ri-instagram-fill text-xl"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
